import { useProgression } from '@/hooks/useProgression';
import { rooms } from '@/data/rooms';

interface RoomProgressIndicatorProps {
  variant?: 'dots' | 'bar' | 'text';
  category?: string;
  className?: string;
}

const RoomProgressIndicator = ({ 
  variant = 'dots', 
  category,
  className = '' 
}: RoomProgressIndicatorProps) => {
  const { state } = useProgression();
  
  const targetRooms = category 
    ? rooms.filter(r => r.category === category)
    : rooms;
  
  const total = targetRooms.length;
  const completed = targetRooms.filter(r => state.completedRooms.includes(r.id)).length;
  const percentage = Math.round((completed / total) * 100);

  if (variant === 'text') {
    return (
      <div className={`text-xs text-muted-foreground/40 ${className}`}>
        <span className="tabular-nums">{completed}</span>
        <span className="text-muted-foreground/20"> of </span>
        <span className="tabular-nums">{total}</span>
        <span className="text-muted-foreground/20"> explored</span>
      </div>
    );
  }

  if (variant === 'bar') {
    return (
      <div className={`w-full ${className}`}>
        <div className="flex justify-between text-xs text-muted-foreground/30 mb-1">
          <span>{completed} / {total} rooms</span>
          <span>{percentage}%</span>
        </div>
        <div className="h-1 bg-border/20 rounded-full overflow-hidden">
          <div 
            className="h-full bg-foreground/30 transition-all duration-700 ease-out"
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    );
  }

  // Dots variant - show first 10 rooms as dots
  const visibleRooms = targetRooms.slice(0, 10);
  const hasMore = total > 10;

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {visibleRooms.map((room, i) => (
        <div
          key={room.id}
          className={`w-2 h-2 rounded-full transition-colors duration-300 ${
            state.completedRooms.includes(room.id)
              ? 'bg-foreground/50'
              : 'bg-border/30'
          }`}
          title={room.title}
        />
      ))}
      {hasMore && (
        <span className="text-xs text-muted-foreground/30 ml-1">
          +{total - 10}
        </span>
      )}
    </div>
  );
};

export default RoomProgressIndicator;
